import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Container, Row, Col, Tabs, Tab, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";

import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';


import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { resourceActions } from '../../actions';
import { educationTaxonomyService } from '../../services';

import PageTitle from "../../components/common/PageTitle";
import AuthImage from "../../components/common/AuthImage";

const ResourcesList = () => {

  const dispatch = useDispatch();
  const loading = useSelector(state => state.resource.loading);
  const imagesList = useSelector(state => state.resource.images);
  const elementsList = useSelector(state => state.resource.elements);
  const vectorsList = useSelector(state => state.resource.vectors);
  const gridsList = useSelector(state => state.resource.grids);
  const framesList = useSelector(state => state.resource.frames);
  const linesList = useSelector(state => state.resource.lines);
  const shapesList = useSelector(state => state.resource.shapes);
  const videosList = useSelector(state => state.resource.videos);
  const backgroundsList = useSelector(state => state.resource.backgrounds);
  const fontsList = useSelector(state => state.resource.fonts);
  const resourceState = useSelector(state => state.resource);
  const [activeTab, setActiveTab] = useState('image')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [editResource, setEditResource] = useState(null)
  const [educationTaxonomy, setEducationTaxonomy] = useState([])
  const uploadFormRef = useRef()


  useEffect(() => {
    dispatch(resourceActions.resetState());
  }, [dispatch])

  useEffect(() => {
    educationTaxonomyService.findAll()
      .then(items => setEducationTaxonomy(Array.isArray(items) ? items : []))
      .catch(() => setEducationTaxonomy([]));
  }, [])

  const selectedGradeCodes = _.get(editResource, 'education.gradeCodes', []);
  const selectedSubjectCodes = _.get(editResource, 'education.subjectCodes', []);
  const gradeOptions = useMemo(
    () => educationTaxonomy.filter(item => item.type === 'grade'),
    [educationTaxonomy],
  );
  const subjectOptions = useMemo(
    () => educationTaxonomy.filter(item => item.type === 'subject'
      && (!(item.gradeCodes || []).length || !selectedGradeCodes.length
        || item.gradeCodes.some(code => selectedGradeCodes.includes(code)))),
    [educationTaxonomy, selectedGradeCodes],
  );
  const topicOptions = useMemo(
    () => educationTaxonomy.filter(item => item.type === 'topic'
      && (!selectedSubjectCodes.length || selectedSubjectCodes.includes(item.subjectCode))
      && (!(item.gradeCodes || []).length || !selectedGradeCodes.length
        || item.gradeCodes.some(code => selectedGradeCodes.includes(code)))),
    [educationTaxonomy, selectedGradeCodes, selectedSubjectCodes],
  );

  const selectedValues = event => Array.from(event.target.options)
    .filter(option => option.selected)
    .map(option => option.value);

  const setEducationSelection = (field, values) => {
    const education = {
      scope: 'legacy',
      gradeCodes: [],
      subjectCodes: [],
      topicCodes: [],
      core: false,
      ..._.get(editResource, 'education', {}),
      [field]: values,
    };
    if (field === 'gradeCodes') {
      const allowedSubjects = educationTaxonomy.filter(item => item.type === 'subject'
        && (!(item.gradeCodes || []).length || !values.length
          || item.gradeCodes.some(code => values.includes(code))));
      education.subjectCodes = education.subjectCodes.filter(code => allowedSubjects.some(item => item.code === code));
      const allowedSubjectCodes = education.subjectCodes;
      education.topicCodes = education.topicCodes.filter(code => educationTaxonomy.some(item => item.type === 'topic'
        && item.code === code
        && (!allowedSubjectCodes.length || allowedSubjectCodes.includes(item.subjectCode))
        && (!(item.gradeCodes || []).length || !values.length
          || item.gradeCodes.some(gradeCode => values.includes(gradeCode)))));
    }
    if (field === 'subjectCodes') {
      education.topicCodes = education.topicCodes.filter(code => educationTaxonomy.some(item => item.type === 'topic'
        && item.code === code
        && (!values.length || values.includes(item.subjectCode))));
    }
    setEditResource({ ...editResource, education });
  };

  useEffect(() => {
    const searchTimer = setTimeout(() => {
      dispatch(resourceActions.search({
        type: _.capitalize(activeTab),
        skip: (page - 1) * pageSize,
        limit: pageSize,
        q: searchTerm,
        educationOnly: true,
      }));
    }, 300);
    return () => clearTimeout(searchTimer);
  }, [activeTab, dispatch, page, pageSize, searchTerm])

  const updateHandler = event => {
    if (event) event.preventDefault();
    const tagsFa = _.get(editResource, 'tagsFa', '').replace(/،/g, ",")
    const tags = _.get(editResource, 'tags', '').replace(/،/g, ",")
    const currentEducation = _.get(editResource, 'education', {});
    const educationScope = currentEducation.scope || 'legacy';
    const params = {
      type: _.get(editResource, 'type'),
      price: _.get(editResource, 'price'),
      invisible: _.get(editResource, 'invisible'),
      tags: tags,
      tagsFa: tagsFa,
      education: {
        scope: educationScope,
        core: educationScope === 'core',
        gradeCodes: educationScope === 'educational' ? (currentEducation.gradeCodes || []) : [],
        subjectCodes: educationScope === 'educational' ? (currentEducation.subjectCodes || []) : [],
        topicCodes: educationScope === 'educational' ? (currentEducation.topicCodes || []) : [],
      },
    }
    updateResource(_.get(editResource, 'id'), params);
    setTimeout(() => {
      setEditResource(null)
    });
  }

  const updateResource = (id, params) => {
    dispatch(resourceActions.updateResource(id, params));
  }
  const changePrice = (resourceId, price) => {
    const resourcePrice = price ? 1 : 0;
    updateResource(resourceId, { price: resourcePrice });
  }
  const changeInvisible = (resourceId, invisible) => {
    updateResource(resourceId, { resourceId, invisible });
  }

  const priceIconTooltip = (props) => (
    <Tooltip id="priceTooltip" {...props}>
      {`${!props.price ? 'Free ' : 'Premium '}`}<span style={{ opacity: .5 }}>(click to change)</span>
    </Tooltip>
  );
  const invisibleIconTooltip = (props) => (
    <Tooltip id="invisibleTooltip" {...props}>
      {`${props.invisible ? 'Unpublished ' : 'Published '}`}<span style={{ opacity: .5 }}>(click to change)</span>
    </Tooltip>
  );

  const columns = [
    {
      text: '#',
      dataField: 'key',
    },
    {
      text: 'Preview',
      dataField: 'src',
      formatter: previewFormatter,
    },
    {
      text: 'Name',
      dataField: 'data.alias',
      formatter: nameFormatter,
    },
    {
      text: 'Published',
      dataField: 'invisible',
      formatter: invisibleFormatter,
    },
    {
      text: 'Premium',
      dataField: 'price',
      formatter: priceFormatter,
    },
    {
      text: 'Ext.',
      dataField: 'data.extension',
    },
    {
      text: 'Size',
      dataField: 'width',
      formatter: sizeFormatter,
    },
    {
      text: 'Tags',
      dataField: 'data.tags',
      formatter: tagsFormatter,
    },
    {
      text: 'Action',
      dataField: 'data.id',
      formatter: actionFormatter,
    }
  ]
  const gridColumns = [
    {
      text: '#',
      dataField: 'key',
    },
    {
      text: 'Preview',
      dataField: 'src',
      formatter: previewFormatter,
    },
    {
      text: 'Name',
      dataField: 'data.alias',
      formatter: nameFormatter,
    },
    {
      text: 'Tags',
      dataField: 'data.tags',
      formatter: tagsFormatter,
    },
    {
      text: 'Action',
      dataField: 'data.id',
      formatter: actionFormatter,
    }
  ]

  function previewFormatter(cell, row) {
    return (
      <AuthImage src={cell} className="tableTempPreview" />
    )
  }
  function nameFormatter(cell, row) {
    return (
      cell ? cell : row.data.name
    )
  }
  function priceFormatter(cell, row) {
    return (
      <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={priceIconTooltip({ price: cell })}
      >
        <span onClick={() => changePrice(row.data.id, !cell)} className={`tableIcon ${!cell ? 'tableFreeIcon' : 'tablePremiumIcon'}`} ></span>
      </OverlayTrigger>
    )
  }
  function invisibleFormatter(cell, row) {
    return (
      <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={invisibleIconTooltip({ invisible: cell })}
      >
        <span onClick={() => changeInvisible(row.data.id, !cell)} className={`tableIcon ${!cell ? 'tablePublishedIcon' : 'tableUnpublishedIcon'}`} ></span>
      </OverlayTrigger>
    )
  }
  function fontPreviewFormatter(cell, row) {
    const src = cell.replace(`/thumbnail.${row.data.extension}`, `--${row.data.extension}/thumbnail.png`)
    return (
      <AuthImage src={src} className="tableTempPreview" />
    )
  }
  function sizeFormatter(cell, row) {
    return (
      `${cell}x${row.height}`
    )
  }
  function tagsFormatter(cell, row) {
    if (!cell) return;
    return (
      <div className="tableTagBox">
        {cell.map((tag, i) => {
          return (
            <span className="tableTags" key={i}>{tag}</span>
          );
        })}
      </div>
    )
  }
  function actionFormatter(cell, row) {
    return (
      <div className="tableTempActionBox">
        <Button className="tableTempAction" onClick={() => editHandler(row)}><i className="material-icons">edit</i> Edit</Button>
        {/* <Button className="tableTempAction" variant="danger" onClick={() => deleteHandler(cell)}><i className="material-icons">delete</i> Delete</Button> */}
      </div>
    )
  }

  const fontColumns = [
    {
      text: '#',
      dataField: 'key',
    },
    {
      text: 'Preview',
      dataField: 'src',
      formatter: fontPreviewFormatter,
    },
    {
      text: 'Name',
      dataField: 'data.alias',
      formatter: nameFormatter,
    },
    {
      text: 'Ext.',
      dataField: 'data.extension',
    },
    {
      text: 'Lang',
      dataField: 'data.lang',
    },
    {
      text: 'Action',
      dataField: 'data.id',
      formatter: actionFormatter,
    }
  ]

  const editHandler = (resource) => {
    // history.push(`resource/${resourceId}/edit`);
    setEditResource({
      id: resource.data.id,
      name: resource.data.name,
      price: resource.price,
      type: resource.type,
      invisible: resource.invisible,
      tags: (resource.data.tags).toString(),
      tagsFa: (resource.data.tagsFa).toString(),
      education: {
        scope: 'legacy',
        gradeCodes: [],
        subjectCodes: [],
        topicCodes: [],
        core: false,
        ..._.get(resource, 'data.education', {}),
      },
    })
  }
  const paginationOptions = resourceKey => ({
    page,
    sizePerPage: pageSize,
    totalSize: resourceState[`${resourceKey}Count`] || 0,
    sizePerPageList: [25, 50, 100],
    paginationTotalRenderer: customTotal,
    showTotal: true,
  });

  function customTotal(from, to, size) {
    return (
      <span className="react-bootstrap-table-pagination-total">
        Showing {from} to {to} of {size} Results
      </span>
    );
  }

  const generalTable = (source, columns, resourceKey) => {
    return (
      <ToolkitProvider keyField="data.id" data={source} columns={columns}>
        {
          props => (
            <div>
              <input
                type="search"
                className="form-control"
                value={activeTab === resourceKey ? searchTerm : ''}
                placeholder="Search resources"
                onChange={event => {
                  setPage(1);
                  setSearchTerm(event.target.value);
                }}
              />
              <hr />
              <BootstrapTable
                {...props.baseProps}
                remote={{ pagination: true }}
                pagination={paginationFactory(paginationOptions(resourceKey))}
                onTableChange={(changeType, tableState) => {
                  if (changeType === 'pagination') {
                    setPage(tableState.page);
                    setPageSize(tableState.sizePerPage);
                  }
                }}
              />
            </div>
          )
        }
      </ToolkitProvider>
    )
  }

  const editForm = () => {
    return (
      <form id="uploadForm" ref={uploadFormRef} className={loading ? 'blur' : undefined} onSubmit={updateHandler}>
        <Row>
          <Col>
            <span>Name:</span>
            <input id="name" name="name" value={_.get(editResource, 'name', '')} disabled readOnly />
            <input id="id" name="id" value={_.get(editResource, 'id', '')} hidden readOnly />
          </Col>
          {/* <Col>
            <span>Type:</span>
            <select id="type" name="type" value={_.get(editResource, 'type')} onChange={(e) => setEditResource({ ...editResource, type: e.target.value })} >
              <option value="Background" hidden={!types.includes('Background')}>Background</option>
              <option value="Image" hidden={!types.includes('Image')}>Image</option>
              <option value="Shape" hidden={!types.includes('Shape')}>Shape</option>
              <option value="Vector" hidden={!types.includes('Sticker')}>Sticker</option>
              <option value="Vector" hidden={!types.includes('Vector')}>Vector</option>
            </select>
          </Col> */}
          <Col>
            <span>Status:</span>
            <select id="invisible" name="invisible" value={_.get(editResource, 'invisible')} onChange={(e) => setEditResource({ ...editResource, invisible: e.target.value })} >
              <option value={false}>Published</option>
              <option value={true}>Unpublished</option>
            </select>
          </Col>
          <Col>
            <span>Value:</span>
            <select id="price" name="price" value={_.get(editResource, 'price')} onChange={(e) => setEditResource({ ...editResource, price: e.target.value })} >
              <option value="0">Free</option>
              <option value="1">Premium</option>
            </select>
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <span>Library:</span>
            <select
              value={_.get(editResource, 'education.scope', 'legacy')}
              disabled={_.get(editResource, 'education.core', false)}
              onChange={event => setEducationSelection('scope', event.target.value)}
            >
              <option value="educational">Educational</option>
              <option value="legacy">Legacy / hidden</option>
              <option value="core">Core editor tool</option>
            </select>
            {_.get(editResource, 'education.core', false) && <small className="d-block text-muted">Core resources are protected.</small>}
          </Col>
          <Col md={3}>
            <span>Grades:</span>
            <select
              multiple
              size="4"
              disabled={_.get(editResource, 'education.scope') !== 'educational'}
              value={selectedGradeCodes}
              onChange={event => setEducationSelection('gradeCodes', selectedValues(event))}
            >
              {gradeOptions.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
            </select>
          </Col>
          <Col md={3}>
            <span>Subjects:</span>
            <select
              multiple
              size="4"
              disabled={_.get(editResource, 'education.scope') !== 'educational'}
              value={selectedSubjectCodes}
              onChange={event => setEducationSelection('subjectCodes', selectedValues(event))}
            >
              {subjectOptions.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
            </select>
          </Col>
          <Col md={3}>
            <span>Topics:</span>
            <select
              multiple
              size="4"
              disabled={_.get(editResource, 'education.scope') !== 'educational'}
              value={_.get(editResource, 'education.topicCodes', [])}
              onChange={event => setEducationSelection('topicCodes', selectedValues(event))}
            >
              {topicOptions.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
            </select>
          </Col>
        </Row>
        <Row>
          <Col>
            <span>Tags (En):</span>
            <textarea id="tags" name="tags" value={_.get(editResource, 'tags')} onChange={(e) => setEditResource({ ...editResource, tags: e.target.value })} />
          </Col>
          <Col>
            <span>Tags (Fa):</span>
            <textarea id="tagsFa" name="tagsFa" value={_.get(editResource, 'tagsFa')} onChange={(e) => setEditResource({ ...editResource, tagsFa: e.target.value })} />
          </Col>
        </Row>
      </form>
    )
  }

  return (
    <Container fluid className="main-content-container px-4">
      {/* Page Header */}
      <Row noGutters className="page-header py-4">
        <PageTitle sm="4" title="Resources List" subtitle="Resources" className="text-sm-left" />
      </Row>
      <Row>
        <Col className={loading ? 'blurContainer' : ''}>
          <Tabs activeKey={activeTab} id="resource-tabs" onSelect={(eventKey) => {
            setActiveTab(eventKey);
            setPage(1);
            setSearchTerm('');
          }}>
            <Tab eventKey="image" title="Images">
              {generalTable(imagesList, columns, 'image')}
            </Tab>
            <Tab eventKey="vector" title="Vectors">
              {generalTable(vectorsList, columns, 'vector')}
            </Tab>
            <Tab eventKey="frame" title="Frames">
              {generalTable(framesList, columns, 'frame')}
            </Tab>
            <Tab eventKey="grid" title="Grids">
              {generalTable(gridsList, gridColumns, 'grid')}
            </Tab>
            <Tab eventKey="shape" title="Shapes">
              {generalTable(shapesList, columns, 'shape')}
            </Tab>
            <Tab eventKey="line" title="Lines">
              {generalTable(linesList, columns, 'line')}
            </Tab>
            <Tab eventKey="video" title="Videos">
              {generalTable(videosList, columns, 'video')}
            </Tab>
            <Tab eventKey="background" title="Backgrounds">
              {generalTable(backgroundsList, columns, 'background')}
            </Tab>
            <Tab eventKey="font" title="Fonts">
              {generalTable(fontsList, fontColumns, 'font')}
            </Tab>
          </Tabs>
        </Col>
      </Row>
      <Row>
        <Col>
        </Col>
      </Row>
      <Modal
        show={Boolean(editResource)}
        // onHide={ }
        backdrop="static"
        aria-labelledby="contained-modal-title-vcenter"
        size="lg"
        centered
      >
        <Modal.Header>
          <Modal.Title>Edit Resource</Modal.Title>
        </Modal.Header>
        <Modal.Body>{editForm()}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setEditResource(null)}>Cancel</Button>
          <Button variant="success" onClick={updateHandler}>Update</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ResourcesList;
